import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { CustomFieldsEditor, EventAdminsPicker } from './CustomFields';
import { toast } from 'sonner';
import { API } from '../../utils/apiConfig';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Upload, X, Loader2, ArrowUp, ArrowDown, Tag, Bell } from 'lucide-react';
import { CATEGORY_FORMATS, EVENT_STATUSES, GENDERS, statusLabel, fileUrl, FOOD_PREFS, SELF_RATINGS } from './eventUtils';

const emptyCategory = () => ({
  name: '', format: 'singles', fee: 0, capacity: 0,
  eligibility: { gender: '', min_age: '', max_age: '', min_dupr: '', max_dupr: '' },
});

const emptyCoupon = () => ({ code: '', discount_type: 'percentage', discount_value: 0, valid_from: '', valid_to: '', is_active: true });

const REQUIRED_FIELD_KEYS = [
  ['gender', 'Gender'], ['dob', 'DOB / Age'], ['city', 'City'],
  ['emergency_contact', 'Emergency Contact'], ['dupr', 'DUPR ID & Rating'],
  ['tshirt', 'T-shirt Size'], ['waiver', 'Waiver Acceptance'],
  ['food', 'Food Preference'], ['self_rating', 'Self Rating'],
];

export function EventFormDialog({ open, onClose, onSaved, event, venues }) {
  const isEdit = !!event;
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(() => event ? {
    ...event,
    categories: (event.categories || []).map((c) => ({
      ...c,
      eligibility: c.eligibility || {},
    })),
    required_fields: event.required_fields || {},
    banners: event.banners || [],
    coupon: event.coupon || emptyCoupon(),
    notify_recipients: event.notify_recipients || [],
    custom_fields: event.custom_fields || [],
    event_admins: event.event_admins || [],
  } : {
    name: '', description: '', venue: '', start_date: '', start_time: '', end_date: '',
    registration_start: '', registration_end: '', rules: '',
    max_categories_per_player: 2, allow_coupons: false, payment_enabled: true,
    show_registration_count: true,
    status: 'draft', banner_file_id: '', banners: [], gallery: [],
    categories: [emptyCategory()], required_fields: {},
    coupon: emptyCoupon(), notify_recipients: [],
    custom_fields: [], event_admins: [],
  });
  const { isEventAdmin } = useAuth();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCoupon = (k, v) => setForm((f) => ({ ...f, coupon: { ...f.coupon, [k]: v } }));

  const uploadImage = async (file, target) => {
    if (!file) return;
    if (target === 'banner' && (form.banners || []).length >= 4) {
      toast.error('Maximum 4 banners per event'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload?category=events`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const id = res.data.id;
      if (target === 'banner') set('banners', [...(form.banners || []), { file_id: id }]);
      else set('gallery', [...(form.gallery || []), { file_id: id }]);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const moveBanner = (i, dir) => {
    setForm((f) => {
      const banners = [...(f.banners || [])];
      const j = i + dir;
      if (j < 0 || j >= banners.length) return f;
      [banners[i], banners[j]] = [banners[j], banners[i]];
      return { ...f, banners };
    });
  };
  const removeBanner = (i) => set('banners', form.banners.filter((_, x) => x !== i));

  const addRecipient = () => set('notify_recipients', [...(form.notify_recipients || []), { name: '', mobile: '' }]);
  const updateRecipient = (i, patch) => {
    const list = [...(form.notify_recipients || [])];
    list[i] = { ...list[i], ...patch };
    set('notify_recipients', list);
  };
  const removeRecipient = (i) => set('notify_recipients', form.notify_recipients.filter((_, x) => x !== i));

  const updateCategory = (i, patch) => {
    setForm((f) => {
      const cats = [...f.categories];
      cats[i] = { ...cats[i], ...patch };
      return { ...f, categories: cats };
    });
  };
  const updateElig = (i, key, val) => {
    setForm((f) => {
      const cats = [...f.categories];
      cats[i] = { ...cats[i], eligibility: { ...(cats[i].eligibility || {}), [key]: val } };
      return { ...f, categories: cats };
    });
  };

  const cleanNum = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Event name is required'); return; }
    if (!form.categories.length || !form.categories.some((c) => c.name.trim())) {
      toast.error('Add at least one category with a name'); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      max_categories_per_player: Number(form.max_categories_per_player) || 1,
      banner_file_id: form.banners?.[0]?.file_id || form.banner_file_id || '',
      coupon: {
        ...form.coupon,
        code: (form.coupon?.code || '').trim().toUpperCase(),
        discount_value: Number(form.coupon?.discount_value) || 0,
      },
      notify_recipients: (form.notify_recipients || []).filter((r) => r.mobile?.trim()),
      custom_fields: (form.custom_fields || []).filter((f) => f.label?.trim()).map((f) => ({ ...(f.id ? { id: f.id } : {}), label: f.label.trim(), mode: f.mode || 'optional' })),
      ...(isEventAdmin ? { event_admins: undefined } : { event_admins: form.event_admins || [] }),
      categories: form.categories.filter((c) => c.name.trim()).map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        name: c.name.trim(), format: c.format, fee: Number(c.fee) || 0,
        capacity: Number(c.capacity) || 0,
        eligibility: {
          gender: c.eligibility?.gender || '',
          min_age: cleanNum(c.eligibility?.min_age),
          max_age: cleanNum(c.eligibility?.max_age),
          min_dupr: cleanNum(c.eligibility?.min_dupr),
          max_dupr: cleanNum(c.eligibility?.max_dupr),
        },
      })),
    };
    try {
      if (isEdit) await axios.put(`${API}/events/${event.id}`, payload);
      else await axios.post(`${API}/events`, payload);
      toast.success(`Event ${isEdit ? 'updated' : 'created'}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Event' : 'Create Event'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Basics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Event Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="event-name" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="event-description" />
            </div>
            <div className="space-y-1.5">
              <Label>Venue</Label>
              <Select value={form.venue || ''} onValueChange={(v) => set('venue', v)}>
                <SelectTrigger data-testid="event-venue"><SelectValue placeholder="Select venue" /></SelectTrigger>
                <SelectContent>
                  {(venues || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="event-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date || ''} onChange={(e) => set('start_date', e.target.value)} data-testid="event-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Start Time</Label>
              <Input type="time" value={form.start_time || ''} onChange={(e) => set('start_time', e.target.value)} data-testid="event-start-time" placeholder="HH:MM" />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date || ''} onChange={(e) => set('end_date', e.target.value)} data-testid="event-end-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Registration Opens</Label>
              <Input type="date" value={form.registration_start || ''} onChange={(e) => set('registration_start', e.target.value)} data-testid="event-reg-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Registration Closes</Label>
              <Input type="date" value={form.registration_end || ''} onChange={(e) => set('registration_end', e.target.value)} data-testid="event-reg-end" />
            </div>
            <div className="space-y-1.5">
              <Label>Max Categories / Player</Label>
              <Input type="number" min="1" value={form.max_categories_per_player} onChange={(e) => set('max_categories_per_player', e.target.value)} data-testid="event-max-cats" />
            </div>
          </div>

          {/* Banner + gallery */}
          <div className="space-y-2">
            <Label>Banners (up to 4 — shown as a slideshow) & Gallery {uploading && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}</Label>
            <div className="flex flex-wrap gap-3 items-center">
              {(form.banners || []).map((b, i) => (
                <div key={b.file_id || i} className="relative" data-testid={`banner-item-${i}`}>
                  <img src={fileUrl(b.file_id)} alt="banner" className="h-20 w-32 object-cover rounded-lg border" />
                  <span className="absolute -top-2 -left-2 bg-primary text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{i + 1}</span>
                  <button onClick={() => removeBanner(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-banner-${i}`}><X className="w-3 h-3" /></button>
                  <div className="absolute bottom-1 right-1 flex gap-0.5">
                    <button onClick={() => moveBanner(i, -1)} disabled={i === 0} className="bg-black/50 text-white rounded p-0.5 disabled:opacity-30" data-testid={`banner-up-${i}`}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveBanner(i, 1)} disabled={i === form.banners.length - 1} className="bg-black/50 text-white rounded p-0.5 disabled:opacity-30" data-testid={`banner-down-${i}`}><ArrowDown className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {(form.banners || []).length < 4 && (
                <label className="h-20 w-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:border-slate-400 text-xs" data-testid="upload-banner">
                  <Upload className="w-4 h-4 mb-1" /> Banner ({(form.banners || []).length}/4)
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], 'banner')} />
                </label>
              )}
              {(form.gallery || []).map((g, i) => (
                <div key={i} className="relative">
                  <img src={fileUrl(g.file_id)} alt="g" className="h-20 w-20 object-cover rounded-lg border" />
                  <button onClick={() => set('gallery', form.gallery.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="h-20 w-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:border-slate-400 text-xs" data-testid="upload-gallery">
                <Plus className="w-4 h-4" /> Gallery
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], 'gallery')} />
              </label>
            </div>
          </div>

          {/* Rules */}
          <div className="space-y-1.5">
            <Label>Rules</Label>
            <Textarea rows={3} value={form.rules || ''} onChange={(e) => set('rules', e.target.value)} data-testid="event-rules" />
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={!!form.payment_enabled} onCheckedChange={(v) => set('payment_enabled', v)} data-testid="event-payment-enabled" />
              <Label className="cursor-pointer">Online Payment (Cashfree)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.allow_coupons} onCheckedChange={(v) => set('allow_coupons', v)} data-testid="event-allow-coupons" />
              <Label className="cursor-pointer">Allow Coupons</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.show_registration_count !== false} onCheckedChange={(v) => set('show_registration_count', v)} data-testid="event-show-reg-count" />
              <Label className="cursor-pointer">Show Registration Count (public page)</Label>
            </div>
          </div>

          {/* Discount Coupon */}
          {form.allow_coupons && (
            <div className="p-3 border rounded-lg space-y-3 bg-emerald-50/40" data-testid="coupon-section">
              <Label className="flex items-center gap-1.5"><Tag className="w-4 h-4 text-emerald-600" /> Discount Coupon</Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="space-y-1 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Code</Label>
                  <Input value={form.coupon?.code || ''} onChange={(e) => setCoupon('code', e.target.value.toUpperCase())} placeholder="SAVE10" data-testid="coupon-code" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Discount Type</Label>
                  <Select value={form.coupon?.discount_type || 'percentage'} onValueChange={(v) => setCoupon('discount_type', v)}>
                    <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="flat">Flat (₹)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{form.coupon?.discount_type === 'flat' ? 'Amount (₹)' : 'Discount (%)'}</Label>
                  <Input type="number" value={form.coupon?.discount_value ?? 0} onChange={(e) => setCoupon('discount_value', e.target.value)} data-testid="coupon-value" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Valid From</Label><Input type="date" value={form.coupon?.valid_from || ''} onChange={(e) => setCoupon('valid_from', e.target.value)} data-testid="coupon-valid-from" /></div>
                <div className="space-y-1"><Label className="text-xs">Valid To</Label><Input type="date" value={form.coupon?.valid_to || ''} onChange={(e) => setCoupon('valid_to', e.target.value)} data-testid="coupon-valid-to" /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.coupon?.is_active !== false} onCheckedChange={(v) => setCoupon('is_active', v)} data-testid="coupon-active" />
                <Label className="cursor-pointer text-sm">Active</Label>
              </div>
            </div>
          )}

          {/* Event-specific notification recipients */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Bell className="w-4 h-4 text-amber-500" /> Event Notification Recipients (in addition to master settings)</Label>
              <Button type="button" size="sm" variant="outline" onClick={addRecipient} data-testid="add-recipient">
                <Plus className="w-4 h-4 mr-1" /> Add Recipient
              </Button>
            </div>
            {(form.notify_recipients || []).map((r, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`recipient-row-${i}`}>
                <Input placeholder="Name" value={r.name || ''} onChange={(e) => updateRecipient(i, { name: e.target.value })} data-testid={`recipient-name-${i}`} />
                <Input placeholder="Mobile" value={r.mobile || ''} onChange={(e) => updateRecipient(i, { mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid={`recipient-mobile-${i}`} />
                <button onClick={() => removeRecipient(i)} className="text-red-600 shrink-0" data-testid={`remove-recipient-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Required fields — tri-state: Hidden / Optional / Mandatory */}
          <div className="space-y-2">
            <Label>Registration Fields Configuration</Label>
            <p className="text-xs text-slate-400">Set each field to <strong>Hidden</strong> (not shown), <strong>Optional</strong> (shown, not required), or <strong>Mandatory</strong> (shown + required).</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
              {REQUIRED_FIELD_KEYS.map(([k, lbl]) => {
                const val = form.required_fields?.[k];
                const norm = val === true ? 'mandatory' : (val === false || !val ? 'hidden' : val);
                return (
                  <div key={k} className="flex items-center justify-between gap-2 py-1 border-b border-slate-100">
                    <span className="text-xs text-slate-700 font-medium">{lbl}</span>
                    <Select value={norm} onValueChange={(v) => {
                      const next = { ...form.required_fields };
                      if (v === 'hidden') delete next[k]; else next[k] = v;
                      set('required_fields', next);
                    }}>
                      <SelectTrigger className={`h-7 w-[108px] text-xs ${norm === 'mandatory' ? 'border-red-300 text-red-700' : norm === 'optional' ? 'border-amber-300 text-amber-700' : 'text-slate-400'}`} data-testid={`req-${k}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hidden"><span className="text-slate-400 text-xs">Hidden</span></SelectItem>
                        <SelectItem value="optional"><span className="text-amber-600 text-xs">Optional</span></SelectItem>
                        <SelectItem value="mandatory"><span className="text-red-600 text-xs font-semibold">Mandatory</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>

          <CustomFieldsEditor fields={form.custom_fields} onChange={(v) => set('custom_fields', v)} />

          {!isEventAdmin && <EventAdminsPicker value={form.event_admins} onChange={(v) => set('event_admins', v)} />}

          {/* Categories */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Categories</Label>
              <Button type="button" size="sm" variant="outline" onClick={() => set('categories', [...form.categories, emptyCategory()])} data-testid="add-category">
                <Plus className="w-4 h-4 mr-1" /> Add Category
              </Button>
            </div>
            {form.categories.map((c, i) => (
              <div key={i} className="p-3 border rounded-lg space-y-3 bg-slate-50" data-testid={`category-${i}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-xs">Name</Label>
                    <Input value={c.name} onChange={(e) => updateCategory(i, { name: e.target.value })} data-testid={`cat-name-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Format</Label>
                    <Select value={c.format} onValueChange={(v) => updateCategory(i, { format: v })}>
                      <SelectTrigger data-testid={`cat-format-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORY_FORMATS.map((f) => <SelectItem key={f} value={f}>{statusLabel(f)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fee (₹)</Label>
                    <Input type="number" value={c.fee} onChange={(e) => updateCategory(i, { fee: e.target.value })} data-testid={`cat-fee-${i}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Capacity (0=∞)</Label>
                    <Input type="number" value={c.capacity} onChange={(e) => updateCategory(i, { capacity: e.target.value })} data-testid={`cat-capacity-${i}`} />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Elig. Gender</Label>
                    <Select value={c.eligibility?.gender || 'any'} onValueChange={(v) => updateElig(i, 'gender', v === 'any' ? '' : v)}>
                      <SelectTrigger data-testid={`cat-gender-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {GENDERS.map((g) => <SelectItem key={g} value={g}>{statusLabel(g)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Min Age</Label><Input type="number" value={c.eligibility?.min_age ?? ''} onChange={(e) => updateElig(i, 'min_age', e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Max Age</Label><Input type="number" value={c.eligibility?.max_age ?? ''} onChange={(e) => updateElig(i, 'max_age', e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Min DUPR</Label><Input type="number" step="0.1" value={c.eligibility?.min_dupr ?? ''} onChange={(e) => updateElig(i, 'min_dupr', e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Max DUPR</Label><Input type="number" step="0.1" value={c.eligibility?.max_dupr ?? ''} onChange={(e) => updateElig(i, 'max_dupr', e.target.value)} /></div>
                </div>
                {form.categories.length > 1 && (
                  <button onClick={() => set('categories', form.categories.filter((_, x) => x !== i))} className="text-red-600 text-xs flex items-center gap-1" data-testid={`remove-cat-${i}`}>
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="event-form-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="event-form-save">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Event')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
